import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils.js";

/**
 * Sheet — bottom/side drawer built on Radix Dialog.
 *
 * Used as the mobile-native counterpart to <Dialog>: nav drawers, full-screen
 * forms, action lists. Default side is "bottom" because phone reach favors
 * bottom-up gestures; use "left" for navigation drawers.
 *
 * Same primitive in two roles keeps interaction language consistent across
 * mobile chrome and modal forms.
 */
const Sheet = DialogPrimitive.Root;
const SheetTrigger = DialogPrimitive.Trigger;
const SheetClose = DialogPrimitive.Close;
const SheetPortal = DialogPrimitive.Portal;

// Same quiet 50% scrim as DialogOverlay (no blur — full-viewport repaints
// jank the slide on low-end mobile). The shared overlay token keeps the
// transition aligned with the rest of the operational system.
const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/50 duration-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none",
      className,
    )}
    {...props}
  />
));
SheetOverlay.displayName = "SheetOverlay";

const sheetVariants = cva(
  "fixed z-50 flex flex-col gap-3 border bg-background shadow-xl outline-none motion-reduce:animate-none",
  {
    variants: {
      side: {
        // animate-sheet-* are the preset's percentage-travel keyframes
        // (220ms in / 200ms out, cubic-bezier(0.32, 0.72, 0, 1)). Percentage
        // travel moves the sheet exactly its own footprint, so a full-height
        // drawer and a short action list get the same designed glide —
        // tailwindcss-animate's fixed-distance slide-*-96 (24rem) slammed at
        // the default 150ms and popped on anything taller than 24rem.
        bottom:
          "inset-x-0 bottom-0 max-h-[92dvh] rounded-t-2xl border-x-0 border-b-0 pb-safe data-[state=open]:animate-sheet-in-bottom data-[state=closed]:animate-sheet-out-bottom",
        top:
          "inset-x-0 top-0 max-h-[92dvh] rounded-b-2xl border-x-0 border-t-0 pt-safe data-[state=open]:animate-sheet-in-top data-[state=closed]:animate-sheet-out-top",
        left:
          "inset-y-0 left-0 w-[85vw] max-w-sm border-l-0 pb-safe pt-safe data-[state=open]:animate-sheet-in-left data-[state=closed]:animate-sheet-out-left",
        right:
          "inset-y-0 right-0 w-[85vw] max-w-sm border-r-0 pb-safe pt-safe data-[state=open]:animate-sheet-in-right data-[state=closed]:animate-sheet-out-right",
      },
    },
    defaultVariants: { side: "bottom" },
  },
);

export interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  hideClose?: boolean;
}

// No grab handle: the pill universally signals swipe-to-dismiss, which
// Radix Dialog doesn't provide — the sheet closes on overlay tap and
// escape instead.
const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(({ className, children, side = "bottom", hideClose = false, ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(sheetVariants({ side }), className)}
      {...props}
    >
      {children}
      {!hideClose && (
        <DialogPrimitive.Close
          aria-label="Close"
          className="absolute right-2 top-2 inline-flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:right-4 sm:top-4 sm:h-9 sm:w-9"
        >
          <X className="h-4 w-4" />
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </SheetPortal>
));
SheetContent.displayName = "SheetContent";

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col gap-1 pl-5 pr-14 pt-2 text-left", className)} {...props} />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "mt-auto flex flex-col-reverse gap-2 border-t bg-background px-5 py-3 sm:flex-row sm:justify-end",
      className,
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-base font-semibold tracking-tight", className)}
    {...props}
  />
));
SheetTitle.displayName = "SheetTitle";

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
SheetDescription.displayName = "SheetDescription";

const SheetBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  // min-h-0 lets this flex child shrink to the sheet's max-height so
  // long content scrolls inside it instead of running past the viewport.
  <div className={cn("min-h-0 flex-1 overflow-y-auto px-5 py-3", className)} {...props} />
);
SheetBody.displayName = "SheetBody";

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetPortal,
  SheetOverlay,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
  SheetBody,
};
